/**
 * Format: Image
 * -----------------------------------------------------------------------------
 * Formats Shopify storefront API image responses.
 *
 */
import { isCdnUrl } from '~/helpers/general';
import { decodeApiId, transformEdges } from '~/helpers/graphql';

/**
 * Format image array from product response.
 * @param {Object} product - Product response.
 * @param {Array} [images] - Custom array of images for formatting.
 * @returns {Array}
 */
export function formatImages(product, images = false) {
  const imagesArray = images ? images : transformEdges(product?.images);

  if (!imagesArray.length) {
    return [];
  }

  const formattedImages = imagesArray.map((image, index) => {
    return formatImage(image, { index, variants: product.variants });
  });

  return formattedImages;
}

/**
 * Format image.
 * - https://shopify.dev/docs/api/storefront/2024-01/objects/Image
 * @param {Object} image - Image response.
 * @param {Object} [data] - Additional data.
 * @param {Number} [data.index] - Forloop index.
 * @param {Array} [data.variants] - Variants response to find linked image.
 * @returns {Object}
 */
export function formatImage(image, { index, variants } = {}) {
  if (!image?.url) {
    return {};
  }

  const linkedVariant = variants?.length
    ? transformEdges(variants).find((variant) => {
        return variant.image.id === image.id;
      })
    : false;

  return {
    alt: image.altText,
    aspectRatio: image.width / image.height,
    height: image.height,
    id: decodeApiId(image.id),
    index,
    presentation: {},
    src: image.url || image.src,
    variant: decodeApiId(linkedVariant?.id) || false,
    width: image.width,
  };
}

/**
 * Format Shopify CDN image URL.
 * @param {String} src - Image URL, can include existing parameters.
 * @param {Object|String} options - Image options object, or image size string.
 * @param {String} options.crop - Crop image positioning when height and width
 * are set.
 * @param {Number} options.height - Height of image in pixels.
 * @param {String} options.pad_color - Background colour to pad image when image
 * cannot be cropped.
 * @param {Number} options.width - Width of image in pixels.
 * @returns {String}
 */
export function formatImageUrl(src, options) {
  if (!isCdnUrl(src)) {
    return src;
  }

  const filetype = src.split('.').reverse()[0]?.split('?')[0];
  let version = src.match(/(?!\?|&)(?<version>v=\d+)/gi);

  if (!filetype || !version) {
    return src;
  }

  version = version[0];
  const parameters = [];

  /**
   * Clean up image URL.
   * - Remove URL parameters.
   * - Standardise protocol.
   * - Remove progressive filetype prefix.
   * - Remove filetype.
   * - Remove sizing, crop, and scale.
   */
  let url = src
    .split('?')[0]
    .split('//')[1]
    .replace('.progressive.', '.')
    .replace(`.${filetype}`, '.')
    .replace(
      /(?<size>_(?:\d+)?x(?:\d+)?)?(?<crop>_crop_(?:top|center|bottom|left|right))?(?<scale>@(?:2|3)x)?\./g,
      '.',
    );

  url = `//${url}${filetype}?${version}`;

  /**
   * If no options provided then return URL.
   */
  if (!options) {
    return url;
  }

  /**
   * Get parameters from options object.
   */
  if (typeof options === 'object') {
    Object.entries(options).forEach(([key, value]) => {
      if (!value || key === 'format') {
        return;
      }

      let formattedValue = value;

      if (key === 'pad_color') {
        formattedValue = value.replace('#', '');
      }

      parameters.push(`${key}=${formattedValue}`);
    });
  }

  /**
   * Get width and height parameters from options string.
   */
  if (typeof options === 'string') {
    const sizes = options.split('x');

    if (sizes[0]) {
      parameters.push(`width=${sizes[0]}`);
    }

    if (sizes[1]) {
      parameters.push(`height=${sizes[1]}`);
    }

    if (sizes[0] && sizes[1]) {
      parameters.push('crop=center');
    }
  }

  return parameters.length ? `${url}&${parameters.join('&')}` : url;
}
